import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileUp, Mail, MessageCircle, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import AppShell from "../components/AppShell";
import DatePickerField from "../components/DatePickerField";
import { api } from "../lib/api";

const emptyClient = {
  name: "",
  client_type: "Persona física",
  rfc: "",
  tax_regime: "",
  email: "",
  phone: "",
  whatsapp: "",
  address: "",
  status: "Activo",
};

const money = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 0,
});

function clientInitials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((word) => word[0]).join("").toUpperCase() || "CL";
}

export default function ClientesPage() {
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [form, setForm] = useState(emptyClient);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteReminder, setNoteReminder] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [editingNoteDate, setEditingNoteDate] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const uploadInput = useRef(null);

  const refresh = useCallback(async (search = "") => {
    try {
      const response = await api.get("/clients", { params: { q: search }, withCredentials: true });
      setClients(response.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar tus clientes.");
    }
  }, []);

  const loadClientDetails = async (client) => {
    setSelectedClient(client);
    setForm({ ...emptyClient, ...client });
    setIsEditing(false);
    setIsCreating(false);
    setNotice("");
    setError("");
    try {
      const [documentsResponse, invoicesResponse, notesResponse] = await Promise.all([
        api.get(`/clients/${client.id}/documents`, { withCredentials: true }),
        api.get(`/clients/${client.id}/invoices`, { withCredentials: true }),
        api.get(`/clients/${client.id}/notes`, { withCredentials: true }),
      ]);
      setDocuments(documentsResponse.data);
      setInvoices(invoicesResponse.data);
      setNotes(notesResponse.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar el detalle del cliente.");
    }
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => refresh(query), 250);
    return () => window.clearTimeout(timeout);
  }, [query, refresh]);

  const activeCount = useMemo(
    () => clients.filter((client) => client.status !== "Inactivo").length,
    [clients],
  );

  const openCreate = () => {
    setSelectedClient(null);
    setForm(emptyClient);
    setDocuments([]);
    setInvoices([]);
    setNotes([]);
    setError("");
    setNotice("");
    setIsCreating(true);
    setIsEditing(true);
  };

  const saveClient = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const response = selectedClient
        ? await api.put(`/clients/${selectedClient.id}`, form, { withCredentials: true })
        : await api.post("/clients", form, { withCredentials: true });
      await refresh();
      if (selectedClient) {
        setSelectedClient(response.data);
        setForm({ ...emptyClient, ...response.data });
        setIsEditing(false);
        setNotice("Los datos del cliente quedaron guardados.");
      } else {
        setIsCreating(false);
        await loadClientDetails(response.data);
        setNotice("Tu cliente ya está listo.");
      }
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar al cliente.");
    }
  };

  const uploadDocument = async (file) => {
    if (!file || !selectedClient) return;
    setError("");
    const body = new FormData();
    body.append("file", file);
    body.append("category", "Documento de cliente");
    try {
      const response = await api.post(`/clients/${selectedClient.id}/documents`, body, { withCredentials: true });
      setDocuments((current) => [response.data, ...current]);
      setNotice("Documento guardado en el perfil del cliente.");
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos subir el documento.");
    }
  };

  const saveNote = async (event) => {
    event.preventDefault();
    if (!selectedClient || !noteDraft.trim()) return;
    setError("");
    try {
      const response = await api.post(
        `/clients/${selectedClient.id}/notes`,
        { text: noteDraft, reminder_date: noteReminder || null },
        { withCredentials: true },
      );
      setNotes((current) => [response.data, ...current]);
      setNoteDraft("");
      setNoteReminder("");
      setNotice("Nota guardada.");
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar la nota.");
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text || "");
    setEditingNoteDate(note.reminder_date ? note.reminder_date.slice(0, 10) : "");
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
    setEditingNoteDate("");
  };

  const updateNote = async (event) => {
    event.preventDefault();
    if (!selectedClient || !editingNoteId) return;
    setError("");
    try {
      const response = await api.put(
        `/clients/${selectedClient.id}/notes/${editingNoteId}`,
        { text: editingNoteText, reminder_date: editingNoteDate || null },
        { withCredentials: true },
      );
      setNotes((current) => current.map((note) => (note.id === editingNoteId ? response.data : note)));
      cancelEditNote();
      setNotice("Nota actualizada.");
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos actualizar la nota.");
    }
  };

  const deleteNote = async (noteId) => {
    if (!selectedClient) return;
    setError("");
    try {
      await api.delete(`/clients/${selectedClient.id}/notes/${noteId}`, { withCredentials: true });
      setNotes((current) => current.filter((note) => note.id !== noteId));
      if (editingNoteId === noteId) cancelEditNote();
      setNotice("Nota eliminada.");
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos eliminar la nota.");
    }
  };

  const messageUrl = selectedClient?.whatsapp || selectedClient?.phone
    ? `https://wa.me/${(selectedClient.whatsapp || selectedClient.phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Hola ${selectedClient.name}, te escribo desde Mi Contador.`)}`
    : "";
  const emailUrl = selectedClient?.email
    ? `mailto:${selectedClient.email}?subject=${encodeURIComponent("Hola desde Mi Contador")}&body=${encodeURIComponent(`Hola ${selectedClient.name},\n\nTe escribo para dar seguimiento.`)}`
    : "";

  return (
    <AppShell>
      <main className="page clients-page">
        <header className="clients-hero">
          <div>
            <small className="eyebrow">TUS RELACIONES</small>
            <h1>Clientes, sin<br /><em>enredos.</em></h1>
            <p>Ten sus datos, documentos y facturas a la mano.</p>
          </div>
          <button className="clients-add-button" data-testid="clients-add-button" onClick={openCreate}>
            <Plus size={19} />
            Nuevo cliente
          </button>
        </header>

        <section className="clients-overview" aria-label="Resumen de clientes">
          <article data-testid="clients-total-count">
            <small>CLIENTES GUARDADOS</small>
            <strong>{clients.length}</strong>
          </article>
          <article data-testid="clients-active-count">
            <small>ACTIVOS</small>
            <strong>{activeCount}</strong>
          </article>
          <p>Busca por nombre, RFC, tipo o estado.</p>
        </section>

        <div className="clients-workspace">
          <section className="clients-list-panel">
            <label className="clients-search" htmlFor="clients-search-input">
              <Search size={18} />
              <input
                id="clients-search-input"
                data-testid="clients-search-input"
                placeholder="Buscar cliente"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            {error && !selectedClient && <p className="clients-error" data-testid="clients-page-error">{error}</p>}
            <div className="clients-list" data-testid="clients-list">
              {clients.map((client) => (
                <button
                  className={`client-row ${selectedClient?.id === client.id ? "selected" : ""}`}
                  data-testid={`client-row-${client.id}`}
                  key={client.id}
                  onClick={() => loadClientDetails(client)}
                >
                  <span className="client-avatar">{clientInitials(client.name)}</span>
                  <span className="client-row-copy">
                    <b>{client.name}</b>
                    <small>{client.rfc || "RFC pendiente"}</small>
                  </span>
                  <i className={`client-status ${client.status === "Inactivo" ? "inactive" : ""}`}>{client.status || "Activo"}</i>
                </button>
              ))}
              {!clients.length && (
                <div className="clients-empty" data-testid="clients-empty-state">
                  <img src="/mi-contador-mark.png" alt="" className="empty-brand-mark" aria-hidden="true" />
                  <b>Aquí aparecerán tus clientes.</b>
                  <span>Crea el primero para empezar.</span>
                </div>
              )}
            </div>
          </section>

          <section className="client-detail-panel" data-testid="client-detail-panel">
            {(isCreating || selectedClient) ? (
              <>
                <div className="client-detail-head">
                  <div>
                    <small className="eyebrow">{isCreating ? "NUEVO CLIENTE" : "PERFIL DEL CLIENTE"}</small>
                    <h2>{isCreating ? "Vamos a conocerlo" : selectedClient.name}</h2>
                  </div>
                  {!isCreating && !isEditing && (
                    <button className="client-icon-button" data-testid="client-edit-button" onClick={() => setIsEditing(true)} aria-label="Editar cliente">
                      <Pencil size={18} />
                    </button>
                  )}
                </div>

                {notice && <p className="clients-notice" data-testid="client-success-notice">{notice}</p>}
                {error && <p className="clients-error" data-testid="client-detail-error">{error}</p>}

                {isEditing ? (
                  <form className="client-form" onSubmit={saveClient} data-testid="client-form">
                    <label htmlFor="client-name">Nombre o razón social
                      <input id="client-name" data-testid="client-name-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
                    </label>
                    <div className="client-form-grid">
                      <label htmlFor="client-type">Tipo de cliente
                        <select id="client-type" data-testid="client-type-select" value={form.client_type} onChange={(event) => setForm({ ...form, client_type: event.target.value })}>
                          <option>Persona física</option>
                          <option>Persona moral</option>
                        </select>
                      </label>
                      <label htmlFor="client-status">Estado
                        <select id="client-status" data-testid="client-status-select" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                          <option>Activo</option>
                          <option>Prospecto</option>
                          <option>Inactivo</option>
                        </select>
                      </label>
                    </div>
                    <div className="client-form-grid">
                      <label htmlFor="client-rfc">RFC
                        <input id="client-rfc" data-testid="client-rfc-input" value={form.rfc} onChange={(event) => setForm({ ...form, rfc: event.target.value.toUpperCase() })} />
                      </label>
                      <label htmlFor="client-regime">Régimen fiscal
                        <input id="client-regime" data-testid="client-regime-input" value={form.tax_regime} onChange={(event) => setForm({ ...form, tax_regime: event.target.value })} />
                      </label>
                    </div>
                    <label htmlFor="client-email">Correo
                      <input id="client-email" data-testid="client-email-input" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
                    </label>
                    <div className="client-form-grid">
                      <label htmlFor="client-phone">Teléfono
                        <input id="client-phone" data-testid="client-phone-input" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
                      </label>
                      <label htmlFor="client-whatsapp">WhatsApp
                        <input id="client-whatsapp" data-testid="client-whatsapp-input" value={form.whatsapp} onChange={(event) => setForm({ ...form, whatsapp: event.target.value })} />
                      </label>
                    </div>
                    <label htmlFor="client-address">Domicilio
                      <input id="client-address" data-testid="client-address-input" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
                    </label>
                    <div className="client-form-actions">
                      {!isCreating && <button className="client-cancel-button" data-testid="client-cancel-edit-button" type="button" onClick={() => { setForm({ ...emptyClient, ...selectedClient }); setIsEditing(false); }}>Cancelar</button>}
                      <button className="clients-save-button" data-testid="client-save-button">Guardar cliente</button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="client-quick-actions">
                      <a className={!messageUrl ? "disabled" : ""} data-testid="client-whatsapp-link" href={messageUrl || undefined} target="_blank" rel="noreferrer" aria-disabled={!messageUrl} onClick={(event) => !messageUrl && event.preventDefault()}>
                        <MessageCircle size={18} />
                        WhatsApp
                      </a>
                      <a className={!emailUrl ? "disabled" : ""} data-testid="client-email-link" href={emailUrl || undefined} aria-disabled={!emailUrl} onClick={(event) => !emailUrl && event.preventDefault()}>
                        <Mail size={18} />
                        Correo
                      </a>
                    </div>
                    <dl className="client-fields" data-testid="client-profile-info">
                      <div><dt>RFC</dt><dd>{selectedClient.rfc || "Pendiente"}</dd></div>
                      <div><dt>Régimen</dt><dd>{selectedClient.tax_regime || "Pendiente"}</dd></div>
                      <div><dt>Correo</dt><dd>{selectedClient.email || "Pendiente"}</dd></div>
                      <div><dt>Tipo</dt><dd>{selectedClient.client_type || "Persona física"}</dd></div>
                    </dl>
                    <div className="client-section-head">
                      <h3>Documentos</h3>
                      <button className="client-upload-button" data-testid="client-upload-document-button" onClick={() => uploadInput.current?.click()}>
                        <FileUp size={16} />
                        Subir
                      </button>
                      <input ref={uploadInput} data-testid="client-document-input" className="client-hidden-input" type="file" accept="application/pdf,.docx,image/*" onChange={(event) => uploadDocument(event.target.files?.[0])} />
                    </div>
                    <div className="client-document-list" data-testid="client-document-list">
                      {documents.map((document) => <div key={document.id}><FileUp size={15} /><span>{document.name}</span></div>)}
                      {!documents.length && <p>Sin documentos todavía.</p>}
                    </div>
                    <div className="client-section-head"><h3>Notas y recordatorios</h3><span data-testid="client-note-count">{notes.length}</span></div>
                    <form className="client-form" onSubmit={saveNote} data-testid="client-note-form" style={{ padding: 0 }}>
                      <label htmlFor="client-note-text">Nueva nota
                        <input id="client-note-text" data-testid="client-note-input" placeholder="Ej: Llamar la próxima semana" value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} />
                      </label>
                      <div className="client-form-grid">
                        <label htmlFor="client-note-date">Recordar el
                          <DatePickerField value={noteReminder} onChange={setNoteReminder} testId="client-note-date-input" allowClear />
                        </label>
                        <div style={{ display: "flex", alignItems: "flex-end" }}>
                          <button className="clients-save-button" data-testid="client-note-save-button" type="submit">Guardar nota</button>
                        </div>
                      </div>
                    </form>
                    <div className="client-document-list" data-testid="client-note-list">
                      {notes.map((note) => (
                        <div key={note.id} data-testid={`client-note-item-${note.id}`}>
                          {editingNoteId === note.id ? (
                            <form onSubmit={updateNote} style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, width: "100%" }} data-testid="client-note-edit-form">
                              <input data-testid="client-note-edit-text" value={editingNoteText} onChange={(event) => setEditingNoteText(event.target.value)} placeholder="Nota" style={{ padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 10, fontSize: 14 }} />
                              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                <DatePickerField value={editingNoteDate} onChange={setEditingNoteDate} testId="client-note-edit-date" allowClear />
                                <button type="submit" data-testid="client-note-edit-save" className="clients-save-button" style={{ padding: "6px 14px", fontSize: 13 }}>Guardar</button>
                                <button type="button" data-testid="client-note-edit-cancel" className="client-cancel-button" onClick={cancelEditNote} style={{ padding: "6px 14px", fontSize: 13 }}>Cancelar</button>
                              </div>
                            </form>
                          ) : (
                            <>
                              <span style={{ flex: 1 }}>
                                <b>{note.text}</b>
                                {note.reminder_date && <small>Recordar: {note.reminder_date}</small>}
                              </span>
                              <button type="button" data-testid={`client-note-edit-${note.id}`} onClick={() => startEditNote(note)} aria-label="Editar nota" style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, color: "#4169e1" }}>
                                <Pencil size={14} />
                              </button>
                              <button type="button" data-testid={`client-note-delete-${note.id}`} onClick={() => deleteNote(note.id)} aria-label="Eliminar nota" style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, color: "#d7565d" }}>
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                      {!notes.length && <p>Sin notas todavía.</p>}
                    </div>
                    <div className="client-section-head"><h3>Facturas</h3><span data-testid="client-invoice-count">{invoices.length}</span></div>
                    <div className="client-invoice-list" data-testid="client-invoice-list">
                      {invoices.slice(0, 4).map((invoice) => <div key={invoice.id}><span><b>{invoice.folio || "Factura"}</b><small>{invoice.status || "Preparada"}</small></span><strong>{money.format(invoice.total || 0)}</strong></div>)}
                      {!invoices.length && <p>Las facturas de este cliente se verán aquí.</p>}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="client-empty-detail" data-testid="client-detail-empty-state">
                <span>✦</span>
                <h2>Elige un cliente</h2>
                <p>O crea uno nuevo para guardar sus datos importantes.</p>
                <button data-testid="client-empty-create-button" onClick={openCreate}>Crear cliente</button>
              </div>
            )}
          </section>
        </div>
      </main>
    </AppShell>
  );
}